'use client'
import * as React from 'react'
import { VoteButton } from '@web/app/[locale]/clubhouse/[slug]/_components/action-buttons/vote-button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  useSocialFeedCommentAddLikeMutation,
  useSocialFeedCommentRemoveLikeMutation,
} from '@web/gen/feed-post'
import type { SocialComment } from 'codegen/mocks'
import type { Project } from '../../page-client'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useIsLoggedInQuery } from 'auth/hooks'
import { socialListCommentsQueryRootKey, socialGetCommentQueryRootKey } from '@web/gen/feed'

interface Props {
  project: Project
  comment: SocialComment
  size?: 'small' | 'regular'
  parentCommentId?: string
  onVoteChange?: () => void
}

export const ProjectCommentVoteButton = ({
  size,
  project,
  comment,
  parentCommentId,
  onVoteChange,
}: Props) => {
  const isLoggedInQuery = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()
  const queryClient = useQueryClient()
  const socialFeedCommentAddLikeMutation = useSocialFeedCommentAddLikeMutation()
  const socialFeedCommentRemoveLikeMutation = useSocialFeedCommentRemoveLikeMutation()

  const [isLiked, setIsLiked] = React.useState(comment?.isLiked ?? false)
  const [likesCount, setLikesCount] = React.useState(comment?.likesCount ?? 0)

  React.useEffect(() => {
    setIsLiked(comment?.isLiked ?? false)
    setLikesCount(comment?.likesCount ?? 0)
  }, [comment?.isLiked, comment?.likesCount])

  const { mutate: toggleLike, isPending } = useMutation({
    // oxlint-disable-next-line require-await
    mutationFn: async (currentlyLiked: boolean) => {
      return currentlyLiked
        ? socialFeedCommentRemoveLikeMutation.mutateAsync({ input: { CommentId: comment?.id } })
        : socialFeedCommentAddLikeMutation.mutateAsync({ input: { CommentId: comment?.id } })
    },
    // oxlint-disable-next-line require-await
    onMutate: async (currentlyLiked: boolean) => {
      setIsLiked(!currentlyLiked)
      setLikesCount(prev => Math.max(0, currentlyLiked ? prev - 1 : prev + 1))
    },
    onSuccess: () => {
      onVoteChange?.()
      queryClient.invalidateQueries({
        queryKey: [socialListCommentsQueryRootKey],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: [socialGetCommentQueryRootKey],
        exact: false,
      })
    },
    onError: (_, currentlyLiked) => {
      setIsLiked(currentlyLiked)
      setLikesCount(prev => (currentlyLiked ? prev + 1 : prev - 1))
    },
  })

  const handleLikeClick = React.useCallback(() => {
    if (!isLoggedInQuery.isPending && !isLoggedInQuery.data) {
      openModal('register')
      return
    }
    toggleLike(isLiked)
  }, [isLiked, toggleLike, isLoggedInQuery.isPending, isLoggedInQuery.data, openModal])

  return (
    <VoteButton
      size={size}
      isLiked={isLiked}
      upvotes={likesCount}
      onClick={handleLikeClick}
      isActionLoading={isPending}
    />
  )
}
